<template>
    <div class="ui-page">
        <ui-page-header :title="isNew ? 'Incluir processo' : (process.process_number || 'Processo sem número')"
            :subtitle="isNew ? 'Registre a identificação e como a Administração Judicial tomou conhecimento.' : (process.subject || '')"
            back="/outros-processos" back-text="Outros processos">
            <template v-if="isNew">
                <v-btn color="primary" depressed :loading="saving" :disabled="!$allowed('create')" @click="createProcess">
                    Salvar
                </v-btn>
            </template>
            <template v-else-if="process.id">
                <ui-badge :text="process.status_name" :tone="toneOf(statuses, process.status)" class="mr-3" />
                <v-btn outlined color="primary" class="mr-2" :loading="loading" @click="load">
                    <v-icon left small>mdi-refresh</v-icon>
                    Atualizar
                </v-btn>
                <v-btn v-if="canEdit && !isClosed" outlined color="primary" class="mr-2" @click="openEditor('process')">
                    <v-icon left small>mdi-pencil-outline</v-icon>
                    Editar cadastro
                </v-btn>
                <v-menu v-if="canEdit || $allowed('delete')" offset-y>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn icon v-bind="attrs" v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
                    </template>
                    <v-list dense>
                        <v-list-item v-if="canEdit && !isClosed" @click="openEditor('close')">
                            <v-list-item-icon class="mr-2"><v-icon small>mdi-archive-arrow-down-outline</v-icon></v-list-item-icon>
                            <v-list-item-title>Encerrar acompanhamento</v-list-item-title>
                        </v-list-item>
                        <v-list-item v-if="canEdit && isClosed" @click="openEditor('reopen')">
                            <v-list-item-icon class="mr-2"><v-icon small>mdi-archive-arrow-up-outline</v-icon></v-list-item-icon>
                            <v-list-item-title>Reabrir acompanhamento</v-list-item-title>
                        </v-list-item>
                        <v-list-item v-if="$allowed('delete')" @click="removeProcess">
                            <v-list-item-icon class="mr-2"><v-icon small color="error">mdi-delete-outline</v-icon></v-list-item-icon>
                            <v-list-item-title>Excluir processo</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </template>
        </ui-page-header>

        <!-- Inclusão -->
        <v-form v-if="isNew" ref="form" v-model="validForm">
            <other-proceeding-form v-model="form" :catalogs="catalogs" is-new show-client />
        </v-form>

        <!-- Detalhes -->
        <template v-else-if="process.id">
            <v-alert v-if="isClosed" dense text type="info" class="mb-4">
                Acompanhamento encerrado em {{ formatDateTime(process.closed_at) }}<span v-if="process.closed_by_name"> por {{ process.closed_by_name }}</span>:
                {{ process.close_reason }}. Reabra o acompanhamento para alterar.
            </v-alert>

            <div class="op-overview">
                <div class="op-overview__item"><span class="op-overview__label">Caso</span><span class="op-overview__value">{{ process.client_name || '—' }}</span></div>
                <div class="op-overview__item"><span class="op-overview__label">Tipo / natureza</span><span class="op-overview__value">{{ process.process_type_name }} · {{ process.nature_name }}</span></div>
                <div class="op-overview__item"><span class="op-overview__label">Tribunal ou órgão</span><span class="op-overview__value">{{ process.tribunal_or_agency || '—' }}</span></div>
                <div class="op-overview__item"><span class="op-overview__label">Responsável</span><span class="op-overview__value" :class="{ 'op-danger': !process.responsible_name }">{{ process.responsible_name || 'Sem responsável' }}</span></div>
            </div>
            <div class="op-overview op-overview--ops">
                <div class="op-overview__item"><v-icon small class="mr-2">mdi-calendar-clock-outline</v-icon><div><span class="op-overview__label">Próximo prazo</span><span class="op-overview__value"><ui-badge v-if="process.next_due_at" :text="formatDate(process.next_due_at)" :tone="toneOf(dueStates, process.due_state)" /><template v-else>Sem prazo</template></span></div></div>
                <div class="op-overview__item"><v-icon small class="mr-2">mdi-timeline-clock-outline</v-icon><div><span class="op-overview__label">Última movimentação</span><span class="op-overview__value">{{ process.last_movement_at ? formatDate(process.last_movement_at) : '—' }}</span></div></div>
                <div class="op-overview__item"><v-icon small class="mr-2">mdi-clipboard-alert-outline</v-icon><div><span class="op-overview__label">Providências abertas</span><span class="op-overview__value">{{ process.pending_tasks_count }}</span></div></div>
                <div class="op-overview__item"><v-icon small class="mr-2">mdi-email-alert-outline</v-icon><div><span class="op-overview__label">Comunicações sem resposta</span><span class="op-overview__value">{{ process.unanswered_communications_count }}</span></div></div>
            </div>

            <v-tabs v-model="tab" show-arrows class="op-tabs mb-4">
                <v-tab v-for="item in tabs" :key="item.id" :href="`#${item.id}`">
                    <v-icon left small>{{ item.icon }}</v-icon>{{ item.name }}<span v-if="item.count" class="op-tab-count">{{ item.count }}</span>
                </v-tab>
            </v-tabs>

            <!-- Resumo -->
            <template v-if="tab === 'summary'">
                <ui-section title="Dados do processo">
                    <div class="op-info-grid">
                        <div><span class="op-overview__label">Número</span><span>{{ process.process_number || '—' }}</span></div>
                        <div><span class="op-overview__label">Assunto / objeto</span><span>{{ process.subject }}</span></div>
                        <div><span class="op-overview__label">Ramo da Justiça</span><span>{{ process.justice_branch || '—' }}</span></div>
                        <div><span class="op-overview__label">Vara / unidade</span><span>{{ process.court_division || '—' }}</span></div>
                        <div><span class="op-overview__label">Comarca / UF</span><span>{{ [process.district, process.state].filter(Boolean).join(' / ') || '—' }}</span></div>
                        <div><span class="op-overview__label">Cadastrado em</span><span>{{ formatDateTime(process.registered_at || process.created_at) }}</span></div>
                        <div><span class="op-overview__label">Última atualização</span><span>{{ formatDateTime(process.updated_at) }}</span></div>
                        <div><span class="op-overview__label">QGC vigente</span><span>{{ process.qgc_version ? `Versão ${process.qgc_version}` : 'Sem QGC importado' }}</span></div>
                        <div class="op-info-grid__wide"><span class="op-overview__label">Observações internas</span><span class="op-pre">{{ process.internal_notes || '—' }}</span></div>
                    </div>
                </ui-section>

                <ui-section title="Origens do conhecimento" description="Como e quando a Administração Judicial tomou conhecimento do processo.">
                    <template #actions>
                        <v-btn v-if="canEdit && !isClosed" outlined small color="primary" @click="openEditor('knowledge')"><v-icon left small>mdi-plus</v-icon>Nova origem</v-btn>
                    </template>
                    <ui-empty v-if="!process.knowledge_sources.length" icon="mdi-source-branch" text="Nenhuma origem registrada." />
                    <v-simple-table v-else dense>
                        <thead><tr><th>Tipo</th><th>Data</th><th>Remetente</th><th>Canal</th><th>Descrição</th><th>Registrado por</th><th></th></tr></thead>
                        <tbody>
                            <tr v-for="item in process.knowledge_sources" :key="item.id">
                                <td>{{ item.source_type_name }}</td>
                                <td>{{ item.known_at ? formatDateTime(item.known_at) : '—' }}</td>
                                <td>{{ item.sender || '—' }}</td>
                                <td>{{ item.channel || '—' }}</td>
                                <td>{{ item.description || '—' }}</td>
                                <td>{{ item.created_by_name || '—' }}</td>
                                <td class="text-right"><v-btn v-if="canEdit && !isClosed" icon x-small @click="removeEntity('knowledge-sources', item.id, 'origem do conhecimento')"><v-icon small>mdi-delete-outline</v-icon></v-btn></td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </ui-section>
            </template>

            <!-- Partes e créditos -->
            <template v-if="tab === 'parties'">
                <ui-section title="Partes e créditos" description="Partes do processo, créditos vinculados ao QGC e a jornada do credor nos demais módulos.">
                    <template #actions>
                        <v-btn v-if="canEdit && !isClosed" outlined small color="primary" @click="openEditor('party')"><v-icon left small>mdi-account-plus-outline</v-icon>Incluir parte</v-btn>
                    </template>
                    <ui-empty v-if="!process.parties.length" icon="mdi-account-group-outline" text="Nenhuma parte cadastrada." />
                    <div v-for="party in process.parties" :key="party.id" class="op-party">
                        <div class="op-party__head">
                            <div>
                                <div class="ui-cell-main">{{ party.name }} <ui-badge v-if="party.is_debtor" text="Parte devedora" tone="purple" class="ml-2" /></div>
                                <div class="ui-cell-sub">{{ formatRegistration(party.registration) || 'Sem CPF/CNPJ' }} · {{ party.role_name }}<span v-if="party.person_type_name"> · {{ party.person_type_name }}</span></div>
                                <div v-if="party.notes" class="ui-cell-sub">{{ party.notes }}</div>
                            </div>
                            <div v-if="canEdit && !isClosed" class="op-party__actions">
                                <v-btn text small color="primary" @click="openEditor('claim', { party })"><v-icon left small>mdi-link-variant</v-icon>Vincular crédito</v-btn>
                                <v-btn text small color="primary" @click="openEditor('origin', { party })"><v-icon left small>mdi-source-branch</v-icon>Origem do crédito</v-btn>
                                <v-btn text small color="primary" @click="openEditor('impact', { party })"><v-icon left small>mdi-swap-horizontal</v-icon>Reflexo no QGC</v-btn>
                                <v-btn icon small @click="openEditor('party', { party })"><v-icon small>mdi-pencil-outline</v-icon></v-btn>
                                <v-btn icon small @click="removeEntity('parties', party.id, 'parte')"><v-icon small>mdi-delete-outline</v-icon></v-btn>
                            </div>
                        </div>

                        <div class="op-party__grid">
                            <div>
                                <div class="op-subtitle">Créditos vinculados</div>
                                <div v-if="party.own_amount !== null && party.own_amount !== undefined" class="op-line">
                                    <span>Crédito informado neste processo</span>
                                    <strong>{{ formatCurrency(party.own_amount, party.own_currency) }}</strong>
                                    <span class="ui-muted">{{ className(party.own_class) }}</span>
                                </div>
                                <div v-if="!party.claims.length" class="ui-muted ui-small">Nenhum crédito do QGC vinculado.</div>
                                <div v-for="claim in party.claims" :key="claim.link_id" class="op-line">
                                    <span>{{ claim.name }}<span v-if="claim.company_name" class="ui-muted"> · {{ claim.company_name }}</span></span>
                                    <strong>{{ formatCurrency(claim.amount, claim.currency) }}</strong>
                                    <span class="ui-muted">{{ className(claim.type) }} · v{{ claim.version }}</span>
                                    <v-btn v-if="canEdit && !isClosed" icon x-small @click="removeEntity('party-claims', claim.link_id, 'vínculo de crédito')"><v-icon small>mdi-link-variant-off</v-icon></v-btn>
                                </div>

                                <div class="op-subtitle mt-3">Origens do crédito</div>
                                <div v-if="!party.credit_origins.length" class="ui-muted ui-small">Nenhuma origem informada.</div>
                                <div v-for="origin in party.credit_origins" :key="origin.id" class="op-line">
                                    <span>{{ origin.origin_type_name }}<span v-if="origin.description"> · {{ origin.description }}</span></span>
                                    <span class="ui-muted">{{ origin.constituted_at ? formatDate(origin.constituted_at) : '' }} {{ origin.reference || '' }}</span>
                                    <v-btn v-if="canEdit && !isClosed" icon x-small @click="removeEntity('credit-origins', origin.id, 'origem do crédito')"><v-icon small>mdi-delete-outline</v-icon></v-btn>
                                </div>
                            </div>

                            <div>
                                <div class="op-subtitle">Jornada do credor <span class="ui-muted ui-small">(pelo CPF/CNPJ nos demais módulos)</span></div>
                                <div class="op-line">
                                    <span>Cadastro no portal</span>
                                    <span v-if="party.credit_journey.user">
                                        <router-link :to="`/credores/${party.credit_journey.user.id}`">{{ party.credit_journey.user.name }}</router-link>
                                        <span class="ui-muted"> · {{ party.credit_journey.user.email }}</span>
                                    </span>
                                    <span v-else class="ui-muted">Sem cadastro vinculado</span>
                                </div>
                                <div class="op-line">
                                    <span>Hab/Div neste caso</span>
                                    <span v-if="!party.credit_journey.hab_div.length" class="ui-muted">Nenhum pedido</span>
                                    <span v-else>
                                        <router-link v-for="event in party.credit_journey.hab_div" :key="event.id" :to="`/divergencias/${event.id}`" class="mr-2">
                                            {{ eventTypeName(event.type) }} #{{ event.id }} ({{ eventStatusName(event.status) }})
                                        </router-link>
                                    </span>
                                </div>
                                <div class="op-line">
                                    <span>QGC vigente</span>
                                    <span v-if="!party.credit_journey.qgc_claims.length" class="ui-muted">Nenhum crédito com este CPF/CNPJ</span>
                                    <span v-else>
                                        <span v-for="claim in party.credit_journey.qgc_claims" :key="claim.id" class="mr-2">
                                            {{ formatCurrency(claim.amount, claim.currency) }} <span class="ui-muted">({{ className(claim.type) }}<span v-if="claim.company_name"> · {{ claim.company_name }}</span>)</span>
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </ui-section>
            </template>

            <!-- Movimentações -->
            <template v-if="tab === 'movements'">
                <ui-section title="Movimentações" description="Eventos do processo e os prazos decorrentes.">
                    <template #actions>
                        <v-btn v-if="canEdit && !isClosed" outlined small color="primary" @click="openEditor('movement')"><v-icon left small>mdi-plus</v-icon>Registrar movimentação</v-btn>
                    </template>
                    <ui-empty v-if="!process.movements.length" icon="mdi-timeline-outline" text="Nenhuma movimentação registrada." />
                    <v-simple-table v-else dense>
                        <thead><tr><th>Data</th><th>Tipo</th><th>Prazo</th><th>Descrição</th><th>Registrado por</th><th></th></tr></thead>
                        <tbody>
                            <tr v-for="item in process.movements" :key="item.id">
                                <td class="text-no-wrap">{{ formatDateTime(item.occurred_at) }}</td>
                                <td>{{ item.movement_type }}</td>
                                <td class="text-no-wrap">{{ item.due_at ? formatDate(item.due_at) : '—' }}</td>
                                <td class="op-pre">{{ item.description || '—' }}</td>
                                <td>{{ item.created_by_name || '—' }}</td>
                                <td class="text-right"><v-btn v-if="canEdit && !isClosed" icon x-small @click="removeEntity('movements', item.id, 'movimentação')"><v-icon small>mdi-delete-outline</v-icon></v-btn></td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </ui-section>
            </template>

            <!-- Documentos -->
            <template v-if="tab === 'documents'">
                <ui-section title="Documentos" description="Arquivos do processo (PDF, Word, Excel, imagens).">
                    <template #actions>
                        <v-btn v-if="canEdit && !isClosed" outlined small color="primary" @click="openEditor('document')"><v-icon left small>mdi-upload-outline</v-icon>Adicionar documento</v-btn>
                    </template>
                    <ui-empty v-if="!process.documents.length" icon="mdi-file-multiple-outline" text="Nenhum documento anexado." />
                    <v-simple-table v-else dense>
                        <thead><tr><th>Título</th><th>Tipo</th><th>Data do documento</th><th>Arquivo</th><th>Enviado</th><th></th></tr></thead>
                        <tbody>
                            <tr v-for="item in process.documents" :key="item.id">
                                <td>{{ item.title }}</td>
                                <td>{{ item.document_type || '—' }}</td>
                                <td class="text-no-wrap">{{ item.document_date ? formatDate(item.document_date) : '—' }}</td>
                                <td><a href="#" @click.prevent="openDocument(item, true)">{{ item.file_name }}</a></td>
                                <td class="text-no-wrap">{{ formatDateTime(item.created_at) }}<span v-if="item.uploaded_by_name" class="ui-muted"> · {{ item.uploaded_by_name }}</span></td>
                                <td class="text-right text-no-wrap">
                                    <v-btn icon x-small title="Baixar" @click="openDocument(item, false)"><v-icon small>mdi-download</v-icon></v-btn>
                                    <v-btn v-if="canEdit && !isClosed" icon x-small @click="removeEntity('documents', item.id, 'documento')"><v-icon small>mdi-delete-outline</v-icon></v-btn>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </ui-section>
            </template>

            <!-- Comunicações -->
            <template v-if="tab === 'communications'">
                <ui-section title="Comunicações" description="Ofícios, intimações e e-mails recebidos, com prazo de resposta.">
                    <template #actions>
                        <v-btn v-if="canEdit && !isClosed" outlined small color="primary" @click="openEditor('communication')"><v-icon left small>mdi-plus</v-icon>Registrar comunicação</v-btn>
                    </template>
                    <ui-empty v-if="!process.communications.length" icon="mdi-email-outline" text="Nenhuma comunicação registrada." />
                    <v-simple-table v-else dense>
                        <thead><tr><th>Assunto</th><th>Tipo</th><th>Recebida em</th><th>Prazo de resposta</th><th>Responsável</th><th>Status</th><th></th></tr></thead>
                        <tbody>
                            <tr v-for="item in process.communications" :key="item.id" class="op-row-click" @click="canEdit && !isClosed ? openEditor('communication', { communication: item }) : null">
                                <td><div>{{ item.subject }}</div><div v-if="item.sender" class="ui-cell-sub">{{ item.sender }}</div></td>
                                <td>{{ item.communication_type_name }}</td>
                                <td class="text-no-wrap">{{ item.received_at ? formatDateTime(item.received_at) : '—' }}</td>
                                <td class="text-no-wrap">{{ item.response_due_at ? formatDate(item.response_due_at) : '—' }}</td>
                                <td>{{ item.responsible_name || '—' }}</td>
                                <td><ui-badge :text="item.status_name" :tone="toneOf(communicationStatuses, item.status)" /></td>
                                <td class="text-right"><v-icon v-if="canEdit && !isClosed" small>mdi-pencil-outline</v-icon></td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </ui-section>
            </template>

            <!-- Providências -->
            <template v-if="tab === 'tasks'">
                <ui-section title="Providências" description="O que a equipe precisa fazer, com responsável, prioridade e prazo.">
                    <template #actions>
                        <v-btn v-if="canEdit && !isClosed" outlined small color="primary" @click="openEditor('task')"><v-icon left small>mdi-plus</v-icon>Criar providência</v-btn>
                    </template>
                    <ui-empty v-if="!process.tasks.length" icon="mdi-clipboard-check-outline" text="Nenhuma providência cadastrada." />
                    <v-simple-table v-else dense>
                        <thead><tr><th>Título</th><th>Prioridade</th><th>Responsável</th><th>Prazo</th><th>Status</th><th>Reflexo no QGC</th><th></th></tr></thead>
                        <tbody>
                            <tr v-for="item in process.tasks" :key="item.id" class="op-row-click" @click="canEdit && !isClosed ? openEditor('task', { task: item }) : null">
                                <td><div>{{ item.title }}</div><div v-if="item.result" class="ui-cell-sub">Resultado: {{ item.result }}</div></td>
                                <td><ui-badge :text="item.priority_name" :tone="toneOf(taskPriorities, item.priority)" /></td>
                                <td>{{ item.responsible_name || '—' }}</td>
                                <td class="text-no-wrap">{{ item.due_at ? formatDate(item.due_at) : '—' }}</td>
                                <td><ui-badge :text="item.status_name" :tone="toneOf(taskStatuses, item.status)" /></td>
                                <td>{{ item.may_impact_qgc ? 'Pode gerar' : '—' }}</td>
                                <td class="text-right"><v-icon v-if="canEdit && !isClosed" small>mdi-pencil-outline</v-icon></td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </ui-section>
            </template>

            <!-- Reflexos no QGC -->
            <template v-if="tab === 'impacts'">
                <ui-section title="Reflexos no QGC" description="Alterações de crédito decorrentes do processo. O QGC só muda depois da aprovação.">
                    <template #actions>
                        <v-btn v-if="canEdit && !isClosed" outlined small color="primary" :disabled="!process.parties.length" @click="openEditor('impact')"><v-icon left small>mdi-plus</v-icon>Registrar reflexo</v-btn>
                    </template>
                    <v-alert v-if="!process.qgc_version" dense text type="warning" class="mb-3">Este caso ainda não tem QGC importado: os reflexos não poderão ser aplicados.</v-alert>
                    <ui-empty v-if="!process.credit_impacts.length" icon="mdi-swap-horizontal" text="Nenhum reflexo registrado." />
                    <div v-for="item in process.credit_impacts" :key="item.id" class="op-impact">
                        <div class="op-impact__head">
                            <div>
                                <div class="ui-cell-main">{{ item.impact_type_name }} <ui-badge :text="item.status_name" :tone="toneOf(impactStatuses, item.status)" class="ml-2" /></div>
                                <div class="ui-cell-sub">
                                    {{ item.party_name || 'Parte não informada' }}
                                    <span v-if="item.source_claim"> · origem: {{ item.source_claim.name }} ({{ formatCurrency(item.source_claim.amount, item.source_claim.currency) }}, {{ className(item.source_claim.type) }})</span>
                                    <span v-if="item.target_claim"> · destino: {{ item.target_claim.name }} ({{ formatCurrency(item.target_claim.amount, item.target_claim.currency) }})</span>
                                    <span v-else-if="item.target_party_name"> · destino: {{ item.target_party_name }}</span>
                                </div>
                                <div class="ui-cell-sub">
                                    <span v-if="item.amount !== null">Valor: {{ formatCurrency(item.amount, item.currency) }}</span>
                                    <span v-if="item.creditor_type"> · Classe: {{ className(item.creditor_type) }}</span>
                                    <span v-if="item.fact_date"> · Fato em {{ formatDate(item.fact_date) }}</span>
                                    <span v-if="item.document_reference"> · {{ item.document_reference }}</span>
                                </div>
                                <div class="op-pre ui-small mt-1">{{ item.justification }}</div>
                                <div class="ui-cell-sub mt-1">
                                    Registrado por {{ item.requested_by_name || '—' }} em {{ formatDateTime(item.created_at) }}
                                    <span v-if="item.reviewed_at"> · {{ item.status === 3 ? 'Aplicado' : 'Analisado' }} por {{ item.reviewed_by_name || '—' }} em {{ formatDateTime(item.reviewed_at) }}</span>
                                    <span v-if="item.review_notes"> · "{{ item.review_notes }}"</span>
                                </div>
                                <div v-if="item.status === 3 && item.after_data" class="ui-cell-sub mt-1"><strong>No QGC (v{{ item.qgc_version }}):</strong> {{ item.after_data.summary }}</div>
                            </div>
                            <div class="op-party__actions">
                                <template v-if="canEdit && !isClosed && (item.status === 1 || item.status === 4)">
                                    <v-btn text small color="primary" @click="openEditor('impact', { impact: item })"><v-icon left small>mdi-pencil-outline</v-icon>Editar</v-btn>
                                    <v-btn depressed small color="primary" :loading="saving" @click="impactAction(item, 'submit')"><v-icon left small>mdi-send-outline</v-icon>Enviar para aprovação</v-btn>
                                </template>
                                <v-btn v-if="item.status === 2 && canApprove && !isClosed" depressed small color="primary" @click="openEditor('review', { impact: item })"><v-icon left small>mdi-clipboard-check-outline</v-icon>Analisar</v-btn>
                                <v-btn v-if="canEdit && !isClosed && [1, 2, 4].includes(item.status)" text small @click="impactAction(item, 'cancel')">Cancelar</v-btn>
                            </div>
                        </div>
                    </div>
                </ui-section>
            </template>

            <!-- Publicacoes do DJEN pelo numero do processo -->
            <template v-if="tab === 'djen'">
                <djen-publications module="other_proceeding" :record-id="process.id" :can-edit="canEdit && !isClosed" @loaded="(info) => djenUnread = info.unread" @changed="load" />
            </template>

            <!-- Auditoria -->
            <template v-if="tab === 'audit'">
                <ui-section title="Auditoria" description="Tudo o que foi feito neste processo, por quem e quando.">
                    <ui-empty v-if="!process.audits.length" icon="mdi-history" text="Sem registros." />
                    <v-simple-table v-else dense>
                        <thead><tr><th>Data</th><th>Usuário</th><th>Ação</th><th>Descrição</th></tr></thead>
                        <tbody>
                            <tr v-for="item in process.audits" :key="item.id">
                                <td class="text-no-wrap">{{ formatDateTime(item.created_at) }}</td>
                                <td>{{ item.admin_name || '—' }}</td>
                                <td><code class="ui-small">{{ item.action }}</code></td>
                                <td>{{ item.description }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </ui-section>
            </template>
        </template>

        <!-- Diálogo genérico: um por tipo de registro -->
        <v-dialog v-model="editor.open" :max-width="editor.kind === 'review' || editor.kind === 'impact' || editor.kind === 'process' ? 960 : 720" scrollable persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">{{ editorTitle }}</h3>
                        <div class="ui-muted">{{ editorSubtitle }}</div>
                    </div>
                    <v-btn icon small @click="closeEditor"><v-icon>mdi-close</v-icon></v-btn>
                </div>
                <div class="ui-card__body">
                    <v-form ref="editorForm" v-model="editor.valid">
                        <!-- Editar cadastro -->
                        <other-proceeding-form v-if="editor.kind === 'process'" v-model="draft" :catalogs="catalogs" />

                        <!-- Encerrar / reabrir -->
                        <v-textarea v-else-if="editor.kind === 'close' || editor.kind === 'reopen'" v-model="draft.reason" label="Motivo *" rows="3" auto-grow dense outlined
                            hide-details="auto" :rules="[$rules.required]"></v-textarea>

                        <!-- Origem do conhecimento -->
                        <v-row v-else-if="editor.kind === 'knowledge'" dense>
                            <v-col cols="12" md="6"><v-select v-model="draft.source_type" :items="knowledgeTypes" item-value="id" item-text="name" label="Como chegou à AJ? *" dense outlined hide-details="auto" :rules="[$rules.required]"></v-select></v-col>
                            <v-col cols="12" md="6"><v-text-field v-model="draft.known_at" type="datetime-local" label="Data e hora" dense outlined hide-details="auto"></v-text-field></v-col>
                            <v-col cols="12" md="6"><v-text-field v-model="draft.sender" label="Remetente" dense outlined hide-details="auto"></v-text-field></v-col>
                            <v-col cols="12" md="6"><v-text-field v-model="draft.channel" label="Canal" dense outlined hide-details="auto"></v-text-field></v-col>
                            <v-col cols="12"><v-textarea v-model="draft.description" label="Descrição" rows="2" auto-grow dense outlined hide-details="auto"></v-textarea></v-col>
                        </v-row>

                        <!-- Parte -->
                        <v-row v-else-if="editor.kind === 'party'" dense>
                            <v-col v-if="!draft.id" cols="12">
                                <v-autocomplete v-model="claimSearch.selected" :items="claimSearch.items" :loading="claimSearch.loading" :search-input.sync="claimSearch.term"
                                    item-value="id" :item-text="claimLabel" label="Buscar credor no QGC vigente (preenche nome e CPF/CNPJ)" dense outlined hide-details="auto" clearable
                                    no-filter no-data-text="Digite nome ou CPF/CNPJ para buscar" return-object @change="fillPartyFromClaim"></v-autocomplete>
                            </v-col>
                            <v-col cols="12" md="7"><v-text-field v-model="draft.name" label="Nome *" dense outlined hide-details="auto" :rules="[$rules.required]"></v-text-field></v-col>
                            <v-col cols="12" md="5"><v-text-field v-model="draft.registration" label="CPF/CNPJ" dense outlined hide-details="auto"></v-text-field></v-col>
                            <v-col cols="12" md="4"><v-select v-model="draft.person_type" :items="personTypes" item-value="id" item-text="name" label="Tipo" dense outlined hide-details="auto" clearable></v-select></v-col>
                            <v-col cols="12" md="4"><v-select v-model="draft.role" :items="partyRoles" item-value="id" item-text="name" label="Papel no processo *" dense outlined hide-details="auto" :rules="[$rules.required]"></v-select></v-col>
                            <v-col cols="12" md="4"><v-switch v-model="draft.is_debtor" label="Parte devedora" dense hide-details class="mt-1" hint="Ocupa o polo devedor"></v-switch></v-col>
                            <v-col cols="12"><v-textarea v-model="draft.notes" label="Observações" rows="2" auto-grow dense outlined hide-details="auto"></v-textarea></v-col>
                            <v-col cols="12"><v-switch v-model="draft.has_own_credit" label="Informar valor e classe próprios (crédito considerado só neste processo)" dense hide-details class="mt-1"></v-switch></v-col>
                            <template v-if="draft.has_own_credit">
                                <v-col cols="12" md="4"><v-text-field v-model="draft.own_amount" type="number" step="0.01" label="Valor" dense outlined hide-details="auto"></v-text-field></v-col>
                                <v-col cols="12" md="3"><v-select v-model="draft.own_currency" :items="currencies" item-value="id" item-text="name" label="Moeda" dense outlined hide-details="auto"></v-select></v-col>
                                <v-col cols="12" md="5"><v-select v-model="draft.own_class" :items="classes" item-value="id" item-text="name" label="Classe" dense outlined hide-details="auto"></v-select></v-col>
                            </template>
                        </v-row>

                        <!-- Vincular crédito do QGC -->
                        <v-row v-else-if="editor.kind === 'claim'" dense>
                            <v-col cols="12">
                                <v-autocomplete v-model="draft.user_creditor_id" :items="claimSearch.items" :loading="claimSearch.loading" :search-input.sync="claimSearch.term"
                                    item-value="id" :item-text="claimLabel" label="Crédito do QGC vigente *" dense outlined hide-details="auto" no-filter
                                    no-data-text="Digite nome ou CPF/CNPJ para buscar" :rules="[$rules.required]"></v-autocomplete>
                            </v-col>
                        </v-row>

                        <!-- Origem do crédito -->
                        <v-row v-else-if="editor.kind === 'origin'" dense>
                            <v-col cols="12" md="6"><v-select v-model="draft.user_creditor_id" :items="partyClaimItems" item-value="id" item-text="label" label="Crédito relacionado" dense outlined hide-details="auto" clearable></v-select></v-col>
                            <v-col cols="12" md="6"><v-select v-model="draft.origin_type" :items="creditOriginTypes" item-value="id" item-text="name" label="Origem do crédito *" dense outlined hide-details="auto" :rules="[$rules.required]"></v-select></v-col>
                            <v-col cols="12"><v-textarea v-model="draft.description" label="Descrição da origem" rows="2" auto-grow dense outlined hide-details="auto"></v-textarea></v-col>
                            <v-col cols="12" md="4"><v-text-field v-model="draft.constituted_at" type="date" label="Data de constituição" dense outlined hide-details="auto"></v-text-field></v-col>
                            <v-col cols="12" md="4"><v-text-field v-model="draft.reference" label="Número do pedido ou identificador" dense outlined hide-details="auto"></v-text-field></v-col>
                            <v-col cols="12" md="4"><v-text-field v-model="draft.document_reference" label="Referência documental" dense outlined hide-details="auto"></v-text-field></v-col>
                        </v-row>

                        <!-- Movimentação -->
                        <v-row v-else-if="editor.kind === 'movement'" dense>
                            <v-col cols="12" md="6"><v-combobox v-model="draft.movement_type" :items="movementTypeSuggestions" label="Tipo da movimentação *" dense outlined hide-details="auto" :rules="[$rules.required]"></v-combobox></v-col>
                            <v-col cols="12" md="3"><v-text-field v-model="draft.occurred_at" type="datetime-local" label="Data *" dense outlined hide-details="auto" :rules="[$rules.required]"></v-text-field></v-col>
                            <v-col cols="12" md="3"><v-text-field v-model="draft.due_at" type="date" label="Prazo decorrente" dense outlined hide-details="auto"></v-text-field></v-col>
                            <v-col cols="12"><v-textarea v-model="draft.description" label="Descrição" rows="3" auto-grow dense outlined hide-details="auto"></v-textarea></v-col>
                        </v-row>

                        <!-- Documento -->
                        <v-row v-else-if="editor.kind === 'document'" dense>
                            <v-col cols="12"><v-file-input v-model="draft.file" label="Arquivo *" dense outlined hide-details="auto" prepend-icon="" prepend-inner-icon="mdi-paperclip" show-size
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png" :rules="[$rules.required, $rules.fileSize(25)]" @change="onDocumentFile"></v-file-input></v-col>
                            <v-col cols="12" md="6"><v-text-field v-model="draft.title" label="Título *" dense outlined hide-details="auto" :rules="[$rules.required]"></v-text-field></v-col>
                            <v-col cols="12" md="3"><v-text-field v-model="draft.document_type" label="Tipo" dense outlined hide-details="auto" placeholder="Ex.: sentença, cálculo"></v-text-field></v-col>
                            <v-col cols="12" md="3"><v-text-field v-model="draft.document_date" type="date" label="Data do documento" dense outlined hide-details="auto"></v-text-field></v-col>
                        </v-row>

                        <!-- Comunicação -->
                        <v-row v-else-if="editor.kind === 'communication'" dense>
                            <v-col cols="12" md="4"><v-select v-model="draft.communication_type" :items="communicationTypes" item-value="id" item-text="name" label="Tipo *" dense outlined hide-details="auto" :rules="[$rules.required]"></v-select></v-col>
                            <v-col cols="12" md="4"><v-text-field v-model="draft.received_at" type="datetime-local" label="Recebida em" dense outlined hide-details="auto"></v-text-field></v-col>
                            <v-col cols="12" md="4"><v-select v-model="draft.status" :items="communicationStatuses" item-value="id" item-text="name" label="Status" dense outlined hide-details="auto"></v-select></v-col>
                            <v-col cols="12" md="4"><v-text-field v-model="draft.sender" label="Remetente" dense outlined hide-details="auto"></v-text-field></v-col>
                            <v-col cols="12" md="8"><v-text-field v-model="draft.subject" label="Assunto *" dense outlined hide-details="auto" :rules="[$rules.required]"></v-text-field></v-col>
                            <v-col cols="12" md="4"><v-text-field v-model="draft.response_due_at" type="date" label="Prazo de resposta" dense outlined hide-details="auto"></v-text-field></v-col>
                            <v-col cols="12" md="8"><v-autocomplete v-model="draft.responsible_admin_id" :items="catalogs.admins" item-value="id" item-text="name" label="Responsável" dense outlined hide-details="auto" clearable></v-autocomplete></v-col>
                            <v-col cols="12"><v-textarea v-model="draft.requested_action" label="Providência solicitada" rows="2" auto-grow dense outlined hide-details="auto"></v-textarea></v-col>
                            <v-col v-if="draft.id" cols="12"><v-textarea v-model="draft.answer_notes" label="Resposta / observações" rows="2" auto-grow dense outlined hide-details="auto"></v-textarea></v-col>
                        </v-row>

                        <!-- Providência -->
                        <v-row v-else-if="editor.kind === 'task'" dense>
                            <v-col cols="12"><v-text-field v-model="draft.title" label="Título *" dense outlined hide-details="auto" :rules="[$rules.required]"></v-text-field></v-col>
                            <v-col cols="12"><v-textarea v-model="draft.description" label="O que deve ser feito" rows="2" auto-grow dense outlined hide-details="auto"></v-textarea></v-col>
                            <v-col cols="12" md="3"><v-select v-model="draft.priority" :items="taskPriorities" item-value="id" item-text="name" label="Prioridade" dense outlined hide-details="auto"></v-select></v-col>
                            <v-col cols="12" md="5"><v-autocomplete v-model="draft.responsible_admin_id" :items="catalogs.admins" item-value="id" item-text="name" label="Responsável" dense outlined hide-details="auto" clearable></v-autocomplete></v-col>
                            <v-col cols="12" md="4"><v-text-field v-model="draft.due_at" type="date" label="Prazo" dense outlined hide-details="auto"></v-text-field></v-col>
                            <v-col cols="12" md="4"><v-select v-model="draft.status" :items="taskStatuses" item-value="id" item-text="name" label="Status" dense outlined hide-details="auto"></v-select></v-col>
                            <v-col cols="12" md="8"><v-switch v-model="draft.may_impact_qgc" label="Pode gerar reflexo no QGC" dense hide-details class="mt-1" hint="Marque quando a providência puder alterar ou exigir análise de um crédito." persistent-hint></v-switch></v-col>
                            <v-col cols="12"><v-textarea v-model="draft.result" label="Resultado" rows="2" auto-grow dense outlined hide-details="auto"></v-textarea></v-col>
                        </v-row>

                        <!-- Reflexo no QGC -->
                        <v-row v-else-if="editor.kind === 'impact'" dense>
                            <v-col cols="12" md="6">
                                <v-select v-model="draft.impact_type" :items="impactTypes" item-value="id" item-text="name" label="Tipo de reflexo *" dense outlined hide-details="auto" :rules="[$rules.required]"
                                    :hint="impactType ? impactType.hint : ''" persistent-hint @change="onImpactTypeChange"></v-select>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-select v-model="draft.party_id" :items="process.parties" item-value="id" item-text="name" label="Credor / parte *" dense outlined hide-details="auto" :rules="[$rules.required]" @change="onImpactPartyChange"></v-select>
                            </v-col>
                            <v-col v-if="impactNeeds('source')" cols="12">
                                <v-select v-model="draft.source_user_creditor_id" :items="partyClaimItems" item-value="id" item-text="label" label="Crédito de origem no QGC *" dense outlined hide-details="auto" :rules="[$rules.required]"
                                    no-data-text="A parte não tem crédito do QGC vinculado: vincule na aba Partes e créditos."></v-select>
                            </v-col>
                            <template v-if="impactNeeds('target')">
                                <v-col cols="12" md="6">
                                    <v-autocomplete v-model="draft.target_user_creditor_id" :items="claimSearch.items" :loading="claimSearch.loading" :search-input.sync="claimSearch.term"
                                        item-value="id" :item-text="claimLabel" label="Crédito de destino no QGC" dense outlined hide-details="auto" clearable no-filter
                                        no-data-text="Digite nome ou CPF/CNPJ para buscar" hint="Ou informe ao lado a parte que recebe o crédito." persistent-hint></v-autocomplete>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-select v-model="draft.target_party_id" :items="process.parties.filter((p) => p.id !== draft.party_id)" item-value="id" item-text="name" label="Parte de destino (crédito novo)" dense outlined hide-details="auto" clearable></v-select>
                                </v-col>
                            </template>
                            <v-col v-if="impactNeeds('amount')" cols="12" md="4"><v-text-field v-model="draft.amount" type="number" step="0.01" min="0" :label="amountLabel" dense outlined hide-details="auto" :rules="[$rules.required]"></v-text-field></v-col>
                            <v-col v-if="impactNeeds('amount')" cols="12" md="3"><v-select v-model="draft.currency" :items="currencies" item-value="id" item-text="name" label="Moeda" dense outlined hide-details="auto"></v-select></v-col>
                            <v-col v-if="impactNeeds('class')" cols="12" md="5"><v-select v-model="draft.creditor_type" :items="classes" item-value="id" item-text="name" label="Classe *" dense outlined hide-details="auto" :rules="[$rules.required]"></v-select></v-col>
                            <v-col cols="12" md="4"><v-text-field v-model="draft.fact_date" type="date" label="Data do fato (sentença, pagamento, acordo)" dense outlined hide-details="auto"></v-text-field></v-col>
                            <v-col cols="12" md="8"><v-text-field v-model="draft.document_reference" label="Documento comprobatório (referência)" dense outlined hide-details="auto" placeholder="Ex.: sentença de 10/10/2026, evento 45"></v-text-field></v-col>
                            <v-col cols="12"><v-textarea v-model="draft.justification" label="Fundamentação *" rows="3" auto-grow dense outlined hide-details="auto" :rules="[$rules.required]"></v-textarea></v-col>
                            <v-col cols="12"><v-alert dense text type="info" class="mb-0">O reflexo fica pendente até ser enviado para aprovação. O QGC só muda depois que alguém com permissão aprovar.</v-alert></v-col>
                        </v-row>

                        <!-- Analisar reflexo -->
                        <div v-else-if="editor.kind === 'review'">
                            <div v-if="review.loading" class="text-center py-6"><v-progress-circular indeterminate color="primary" /></div>
                            <template v-else-if="review.preview">
                                <v-alert dense text type="info" class="mb-4">{{ review.preview.summary }}</v-alert>
                                <div class="op-review-grid">
                                    <div v-if="review.preview.source_before" class="op-review-card">
                                        <div class="op-subtitle">Crédito de origem · atual</div>
                                        <div>{{ review.preview.source_before.name }}</div>
                                        <div class="ui-cell-sub">{{ formatRegistration(review.preview.source_before.registration) }}</div>
                                        <div><strong>{{ formatCurrency(review.preview.source_before.amount, review.preview.source_before.currency) }}</strong> · {{ className(review.preview.source_before.type) }}</div>
                                    </div>
                                    <div v-if="review.preview.source_after" class="op-review-card" :class="{ 'op-review-card--removed': review.preview.source_after.deleted }">
                                        <div class="op-subtitle">Crédito de origem · após</div>
                                        <template v-if="review.preview.source_after.deleted"><div class="op-danger">Excluído do QGC</div></template>
                                        <template v-else>
                                            <div>{{ review.preview.source_after.name }}</div>
                                            <div><strong>{{ formatCurrency(review.preview.source_after.amount, review.preview.source_after.currency) }}</strong> · {{ className(review.preview.source_after.type) }}</div>
                                        </template>
                                    </div>
                                    <div v-if="review.preview.target_before" class="op-review-card">
                                        <div class="op-subtitle">Crédito de destino · atual</div>
                                        <div>{{ review.preview.target_before.name }}</div>
                                        <div><strong>{{ formatCurrency(review.preview.target_before.amount, review.preview.target_before.currency) }}</strong> · {{ className(review.preview.target_before.type) }}</div>
                                    </div>
                                    <div v-if="review.preview.target_after" class="op-review-card">
                                        <div class="op-subtitle">Crédito de destino · após</div>
                                        <div>{{ review.preview.target_after.name }}</div>
                                        <div><strong>{{ formatCurrency(review.preview.target_after.amount, review.preview.target_after.currency) }}</strong> · {{ className(review.preview.target_after.type) }}</div>
                                    </div>
                                    <div v-if="review.preview.new_claim" class="op-review-card op-review-card--new">
                                        <div class="op-subtitle">Crédito novo no QGC</div>
                                        <div>{{ review.preview.new_claim.name }}</div>
                                        <div class="ui-cell-sub">{{ formatRegistration(review.preview.new_claim.registration) || 'Sem CPF/CNPJ' }}</div>
                                        <div><strong>{{ formatCurrency(review.preview.new_claim.amount, review.preview.new_claim.currency) }}</strong> · {{ className(review.preview.new_claim.type) }}</div>
                                    </div>
                                </div>
                                <div class="op-pre ui-small mt-4"><strong>Fundamentação:</strong> {{ editor.context.impact.justification }}</div>
                                <v-textarea v-model="draft.notes" label="Observação da análise (obrigatória para rejeitar)" rows="2" auto-grow dense outlined hide-details="auto" class="mt-4"></v-textarea>
                            </template>
                        </div>
                    </v-form>
                </div>
                <div class="ui-card__footer">
                    <v-btn text :disabled="saving" @click="closeEditor">Cancelar</v-btn>
                    <v-spacer />
                    <template v-if="editor.kind === 'review'">
                        <v-btn outlined color="error" class="mr-2" :loading="saving" :disabled="review.loading" @click="reviewImpact('reject')"><v-icon left small>mdi-close-circle-outline</v-icon>Rejeitar</v-btn>
                        <v-btn depressed color="success" :loading="saving" :disabled="review.loading || !review.preview" @click="reviewImpact('approve')"><v-icon left small>mdi-check-circle-outline</v-icon>Aprovar e aplicar no QGC</v-btn>
                    </template>
                    <v-btn v-else depressed color="primary" :loading="saving" @click="saveEditor">{{ editorSaveLabel }}</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import _ from 'lodash';
import axios from 'axios';
import OtherProceedingForm from "./OtherProceedingForm.vue";
import DjenPublications from "@/components/djen/DjenPublications.vue";
import creditorClassUtil from "@/common/util/creditor-class.util";
import eventTypes from "@/common/data/event-types.data";
import eventStatuses from "@/common/data/event-status.data";
import {
    statuses, knowledgeTypes, personTypes, partyRoles, creditOriginTypes, communicationTypes, communicationStatuses,
    taskStatuses, taskPriorities, impactTypes, impactStatuses, currencies, dueStates, movementTypeSuggestions, labelOf, toneOf,
} from "@/common/data/other-proceeding.data";

const EDITOR_TITLES = {
    process: ["Editar cadastro", "Atualize os dados de identificação e acompanhamento."],
    close: ["Encerrar acompanhamento", "Informe o motivo para encerrar o acompanhamento deste processo."],
    reopen: ["Reabrir acompanhamento", "Informe o motivo para retomar o acompanhamento."],
    knowledge: ["Nova origem do conhecimento", "Documente como a Administração Judicial tomou conhecimento."],
    party: ["Parte do processo", "Identifique a parte e seu papel neste processo."],
    claim: ["Vincular crédito do QGC", "Busque e selecione o crédito do QGC vigente que será vinculado à parte."],
    origin: ["Origem do crédito", "Negócio, fato ou documento que constituiu o crédito."],
    movement: ["Registrar movimentação", "Registre o evento processual e, quando houver, o prazo decorrente."],
    document: ["Adicionar documento", "Selecione o arquivo e informe os dados para localizá-lo."],
    communication: ["Registrar comunicação", "Informe o documento recebido, o prazo e a providência solicitada."],
    task: ["Providência", "Defina o que deve ser feito, por quem e até quando."],
    impact: ["Reflexo no QGC", "Selecione a operação, a parte e os créditos envolvidos."],
    review: ["Analisar reflexo no QGC", "Confira o crédito atual e o resultado antes de aprovar."],
};

export default {
    name: "OtherProceedingDetails",

    components: { OtherProceedingForm, DjenPublications },

    data: () => ({
        statuses, knowledgeTypes, personTypes, partyRoles, creditOriginTypes, communicationTypes, communicationStatuses,
        taskStatuses, taskPriorities, impactTypes, impactStatuses, currencies, dueStates, movementTypeSuggestions,
        loading: false,
        djenUnread: 0,
        saving: false,
        validForm: false,
        tab: "summary",
        catalogs: { clients: [], admins: [] },
        classes: [],
        process: {},
        form: {},
        editor: { open: false, kind: null, valid: false, context: {} },
        draft: {},
        claimSearch: { term: "", items: [], loading: false, selected: null },
        review: { loading: false, preview: null },
    }),

    computed: {
        isNew() {
            return this.$route.params.id === "novo";
        },
        isClosed() {
            return Number(this.process.status) === 8;
        },
        canEdit() {
            return this.$allowed("edit");
        },
        canApprove() {
            return this.$allowed("otherproceedings.apply_qgc");
        },
        tabs() {
            const p = this.process;
            return [
                { id: "summary", name: "Resumo", icon: "mdi-text-box-outline" },
                { id: "parties", name: "Partes e créditos", icon: "mdi-account-group-outline", count: (p.parties || []).length },
                { id: "movements", name: "Movimentações", icon: "mdi-timeline-outline", count: (p.movements || []).length },
                { id: "documents", name: "Documentos", icon: "mdi-file-multiple-outline", count: (p.documents || []).length },
                { id: "communications", name: "Comunicações", icon: "mdi-email-outline", count: (p.communications || []).length },
                { id: "tasks", name: "Providências", icon: "mdi-clipboard-check-outline", count: (p.tasks || []).length },
                { id: "impacts", name: "Reflexos no QGC", icon: "mdi-swap-horizontal", count: (p.credit_impacts || []).length },
                { id: "djen", name: "Publicações DJEN", icon: "mdi-newspaper-variant-outline", count: this.djenUnread },
                { id: "audit", name: "Auditoria", icon: "mdi-history" },
            ];
        },
        editorTitle() {
            return (EDITOR_TITLES[this.editor.kind] || [""])[0];
        },
        editorSubtitle() {
            return (EDITOR_TITLES[this.editor.kind] || ["", ""])[1];
        },
        editorSaveLabel() {
            if (this.editor.kind === "close") return "Encerrar";
            if (this.editor.kind === "reopen") return "Reabrir";
            if (this.editor.kind === "claim") return "Vincular";
            if (this.editor.kind === "document") return "Enviar";
            return "Salvar";
        },
        impactType() {
            return this.impactTypes.find((item) => item.id === this.draft.impact_type) || null;
        },
        amountLabel() {
            const type = this.draft.impact_type;
            if (type === 2) return "Novo valor do crédito *";
            if (type === 5) return "Valor pago *";
            if (type === 7) return "Valor transferido *";
            return "Valor *";
        },
        // Creditos do QGC vinculados a parte escolhida (origem do reflexo / origem do credito).
        partyClaimItems() {
            const partyId = this.draft.party_id || (this.editor.context.party && this.editor.context.party.id);
            const party = (this.process.parties || []).find((item) => item.id === partyId);
            return party ? party.claims.map((claim) => ({ id: claim.id, label: this.claimLabel(claim) })) : [];
        },
    },

    watch: {
        "$route.params.id"() {
            this.init();
        },
        "claimSearch.term"(value) {
            if (this.editor.open && value && value.length >= 2) this.searchClaimsDebounced();
        },
        tab(value) {
            if (!this.isNew && value && this.$route.query.tab !== value) this.$router.replace({ query: { ...this.$route.query, tab: value, create: undefined } });
        },
    },

    created() {
        this.init();
    },

    methods: {
        toneOf,

        async init() {
            await this.loadCatalogs();
            if (this.isNew) {
                this.form = { client_id: Number(this.$route.query.client_id) || null, nature: 1, process_type: 1, status: 1, state: null, knowledge_source: { source_type: null, known_at: "", sender: "", channel: "", description: "" } };
                return;
            }
            if (this.$route.query.tab) this.tab = this.$route.query.tab;
            await this.load();
            if (this.$route.query.create === "1" && this.canEdit && !this.isClosed) {
                const kind = { movements: "movement", tasks: "task", communications: "communication", parties: "party", impacts: "impact", documents: "document" }[this.tab];
                if (kind) this.openEditor(kind);
            }
        },

        async loadCatalogs() {
            try {
                const result = await axios.get('/api/admin/other-proceedings/catalogs');
                this.catalogs = { clients: result.data.content.clients, admins: result.data.content.admins };
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 3000 });
            }
        },

        async load() {
            this.loading = true;
            try {
                const result = await axios.get(`/api/admin/other-proceedings/${this.$route.params.id}`);
                this.setProcess(result.data.content);
                // Publicacoes do DJEN nao lidas (contador da aba).
                axios.get(`/api/admin/other-proceedings/${this.$route.params.id}/djen`).then((djen) => { this.djenUnread = djen.data.content.unread; }).catch(() => {});
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.loading = false;
            }
        },

        async setProcess(process) {
            this.process = process;
            if (process.client_id && !this.classes.length) {
                try {
                    this.classes = await creditorClassUtil.listByClient(process.client_id);
                } catch (error) {
                    this.classes = creditorClassUtil.predefined;
                }
            }
        },

        // ---------- cadastro ----------

        async createProcess() {
            if (!this.$refs.form.validate()) {
                this.$dialog.notify.warning('Preencha os campos obrigatórios.', { position: 'top-right', timeout: 3000 });
                return;
            }
            this.saving = true;
            try {
                const result = await axios.post('/api/admin/other-proceedings', this.form);
                this.$dialog.notify.success('Processo cadastrado.', { position: 'top-right', timeout: 3000 });
                this.$router.replace(`/outros-processos/${result.data.content.id}`);
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.saving = false;
            }
        },

        async removeProcess() {
            const proceed = await this.$dialog.confirm({
                text: 'O processo e todos os seus registros deixarão de aparecer. Os reflexos já aplicados no QGC não são desfeitos. Continuar?',
                title: 'Excluir processo', actions: { false: 'Cancelar', true: 'Excluir' },
            });
            if (!proceed) return;
            try {
                await axios.delete(`/api/admin/other-proceedings/${this.process.id}`);
                this.$dialog.notify.success('Processo excluído.', { position: 'top-right', timeout: 3000 });
                this.$router.push('/outros-processos');
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            }
        },

        // ---------- dialogo generico ----------

        openEditor(kind, context = {}) {
            this.editor = { open: true, kind, valid: false, context };
            this.claimSearch = { term: "", items: [], loading: false, selected: null };
            this.review = { loading: false, preview: null };
            const p = this.process;
            const pick = (source, keys) => keys.reduce((acc, key) => ({ ...acc, [key]: source ? source[key] : null }), {});
            switch (kind) {
                case "process":
                    this.draft = { ...pick(p, ["nature", "process_type", "process_number", "subject", "tribunal_or_agency", "justice_branch", "court_division", "district", "state", "responsible_admin_id", "status", "internal_notes"]) };
                    break;
                case "close":
                case "reopen":
                    this.draft = { reason: "" };
                    break;
                case "knowledge":
                    this.draft = { source_type: null, known_at: "", sender: "", channel: "", description: "" };
                    break;
                case "party": {
                    const party = context.party;
                    this.draft = party
                        ? { ...pick(party, ["id", "name", "registration", "person_type", "role", "notes", "own_amount", "own_currency", "own_class"]), is_debtor: !!party.is_debtor, has_own_credit: party.own_amount !== null && party.own_amount !== undefined }
                        : { name: "", registration: "", person_type: null, role: 1, is_debtor: false, notes: "", has_own_credit: false, own_amount: null, own_currency: 1, own_class: null, claim_ids: [] };
                    break;
                }
                case "claim":
                    this.draft = { party_id: context.party.id, user_creditor_id: null };
                    break;
                case "origin":
                    this.draft = { party_id: context.party.id, user_creditor_id: null, origin_type: null, description: "", constituted_at: "", reference: "", document_reference: "" };
                    break;
                case "movement":
                    this.draft = { movement_type: "", occurred_at: this.$moment().format('YYYY-MM-DDTHH:mm'), due_at: "", description: "" };
                    break;
                case "document":
                    this.draft = { file: null, title: "", document_type: "", document_date: "" };
                    break;
                case "communication": {
                    const c = context.communication;
                    this.draft = c
                        ? { ...pick(c, ["id", "communication_type", "status", "sender", "subject", "response_due_at", "responsible_admin_id", "requested_action", "answer_notes"]), received_at: this.toInputDateTime(c.received_at), response_due_at: this.toInputDate(c.response_due_at) }
                        : { communication_type: null, received_at: this.$moment().format('YYYY-MM-DDTHH:mm'), status: 1, sender: "", subject: "", response_due_at: "", responsible_admin_id: null, requested_action: "" };
                    break;
                }
                case "task": {
                    const t = context.task;
                    this.draft = t
                        ? { ...pick(t, ["id", "title", "description", "priority", "responsible_admin_id", "status", "result"]), due_at: this.toInputDate(t.due_at), may_impact_qgc: !!t.may_impact_qgc }
                        : { title: "", description: "", priority: 2, responsible_admin_id: null, due_at: "", status: 1, result: "", may_impact_qgc: false };
                    break;
                }
                case "impact": {
                    const i = context.impact;
                    this.draft = i
                        ? { ...pick(i, ["id", "impact_type", "party_id", "source_user_creditor_id", "target_user_creditor_id", "target_party_id", "amount", "currency", "creditor_type", "document_reference", "justification"]), fact_date: this.toInputDate(i.fact_date) }
                        : { impact_type: null, party_id: context.party ? context.party.id : null, source_user_creditor_id: null, target_user_creditor_id: null, target_party_id: null, amount: null, currency: 1, creditor_type: null, fact_date: "", document_reference: "", justification: "" };
                    if (i && i.target_claim) this.claimSearch.items = [i.target_claim];
                    break;
                }
                case "review":
                    this.draft = { notes: "" };
                    this.loadReview(context.impact);
                    break;
                default:
                    this.draft = {};
            }
        },

        closeEditor() {
            this.editor.open = false;
        },

        async saveEditor() {
            if (this.$refs.editorForm && !this.$refs.editorForm.validate()) {
                this.$dialog.notify.warning('Preencha os campos obrigatórios.', { position: 'top-right', timeout: 3000 });
                return;
            }
            const id = this.process.id;
            const kind = this.editor.kind;
            const d = this.draft;
            this.saving = true;
            try {
                let result;
                if (kind === "process") result = await axios.patch(`/api/admin/other-proceedings/${id}`, d);
                else if (kind === "close") result = await axios.post(`/api/admin/other-proceedings/${id}/close`, d);
                else if (kind === "reopen") result = await axios.post(`/api/admin/other-proceedings/${id}/reopen`, d);
                else if (kind === "knowledge") result = await axios.post(`/api/admin/other-proceedings/${id}/knowledge-sources`, d);
                else if (kind === "party") {
                    const payload = { ...d, own_amount: d.has_own_credit ? d.own_amount : null, own_class: d.has_own_credit ? d.own_class : null, own_currency: d.has_own_credit ? d.own_currency : null };
                    result = d.id ? await axios.patch(`/api/admin/other-proceedings/${id}/parties/${d.id}`, payload) : await axios.post(`/api/admin/other-proceedings/${id}/parties`, payload);
                } else if (kind === "claim") result = await axios.post(`/api/admin/other-proceedings/${id}/parties/${d.party_id}/claims`, { user_creditor_id: d.user_creditor_id });
                else if (kind === "origin") result = await axios.post(`/api/admin/other-proceedings/${id}/credit-origins`, d);
                else if (kind === "movement") result = await axios.post(`/api/admin/other-proceedings/${id}/movements`, d);
                else if (kind === "document") {
                    const fd = new FormData();
                    fd.append('file', d.file);
                    fd.append('title', d.title || '');
                    fd.append('document_type', d.document_type || '');
                    fd.append('document_date', d.document_date || '');
                    result = await axios.post(`/api/admin/other-proceedings/${id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                } else if (kind === "communication") result = d.id ? await axios.patch(`/api/admin/other-proceedings/${id}/communications/${d.id}`, d) : await axios.post(`/api/admin/other-proceedings/${id}/communications`, d);
                else if (kind === "task") result = d.id ? await axios.patch(`/api/admin/other-proceedings/${id}/tasks/${d.id}`, d) : await axios.post(`/api/admin/other-proceedings/${id}/tasks`, d);
                else if (kind === "impact") result = d.id ? await axios.patch(`/api/admin/other-proceedings/${id}/credit-impacts/${d.id}`, d) : await axios.post(`/api/admin/other-proceedings/${id}/credit-impacts`, d);
                if (result) await this.setProcess(result.data.content);
                this.editor.open = false;
                this.$dialog.notify.success('Registro salvo.', { position: 'top-right', timeout: 2500 });
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.saving = false;
            }
        },

        async removeEntity(resource, entityId, label) {
            const proceed = await this.$dialog.confirm({ text: `Remover ${label}?`, title: 'Confirmar', actions: { false: 'Cancelar', true: 'Remover' } });
            if (!proceed) return;
            this.saving = true;
            try {
                const result = await axios.delete(`/api/admin/other-proceedings/${this.process.id}/${resource}/${entityId}`);
                await this.setProcess(result.data.content);
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.saving = false;
            }
        },

        async openDocument(item, inline) {
            try {
                const result = await axios.get(`/api/admin/other-proceedings/${this.process.id}/documents/${item.id}/url`, { params: { inline: inline ? 1 : 0 } });
                window.open(result.data.content.url, '_blank');
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            }
        },

        onDocumentFile(file) {
            if (file && !this.draft.title) this.draft.title = String(file.name || '').replace(/\.[^.]+$/, '');
        },

        // ---------- reflexos ----------

        impactNeeds(field) {
            return !!(this.impactType && this.impactType.needs.includes(field));
        },

        onImpactTypeChange() {
            if (!this.impactNeeds("source")) this.draft.source_user_creditor_id = null;
            if (!this.impactNeeds("target")) { this.draft.target_user_creditor_id = null; this.draft.target_party_id = null; }
            if (!this.impactNeeds("amount")) this.draft.amount = null;
            if (!this.impactNeeds("class")) this.draft.creditor_type = null;
        },

        onImpactPartyChange() {
            this.draft.source_user_creditor_id = null;
            if (this.partyClaimItems.length === 1) this.draft.source_user_creditor_id = this.partyClaimItems[0].id;
        },

        async impactAction(item, action) {
            if (action === "cancel") {
                const proceed = await this.$dialog.confirm({ text: 'Cancelar este reflexo?', title: 'Confirmar', actions: { false: 'Voltar', true: 'Cancelar reflexo' } });
                if (!proceed) return;
            }
            this.saving = true;
            try {
                const result = await axios.post(`/api/admin/other-proceedings/${this.process.id}/credit-impacts/${item.id}/${action}`);
                await this.setProcess(result.data.content);
                this.$dialog.notify.success(action === "submit" ? 'Reflexo enviado para aprovação.' : 'Reflexo cancelado.', { position: 'top-right', timeout: 3000 });
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.saving = false;
            }
        },

        async loadReview(impact) {
            this.review.loading = true;
            try {
                const result = await axios.get(`/api/admin/other-proceedings/${this.process.id}/credit-impacts/${impact.id}/preview`);
                this.review.preview = result.data.content;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
                this.editor.open = false;
            } finally {
                this.review.loading = false;
            }
        },

        async reviewImpact(decision) {
            const impact = this.editor.context.impact;
            if (decision === "reject" && !String(this.draft.notes || '').trim()) {
                this.$dialog.notify.warning('Informe a observação para rejeitar.', { position: 'top-right', timeout: 3000 });
                return;
            }
            if (decision === "approve") {
                const proceed = await this.$dialog.confirm({
                    text: `${this.review.preview.summary} Esta alteração entra no QGC vigente e fica registrada no histórico do crédito. Confirmar?`,
                    title: 'Aprovar e aplicar no QGC', actions: { false: 'Voltar', true: 'Aprovar e aplicar' },
                });
                if (!proceed) return;
            }
            this.saving = true;
            try {
                const result = await axios.post(`/api/admin/other-proceedings/${this.process.id}/credit-impacts/${impact.id}/${decision}`, { notes: this.draft.notes });
                await this.setProcess(result.data.content);
                this.editor.open = false;
                this.$dialog.notify.success(decision === "approve" ? 'Reflexo aplicado no QGC.' : 'Reflexo rejeitado.', { position: 'top-right', timeout: 3000 });
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 6000 });
            } finally {
                this.saving = false;
            }
        },

        // ---------- busca de creditos no QGC ----------

        searchClaimsDebounced: _.debounce(function () {
            this.searchClaims();
        }, 400),

        async searchClaims() {
            if (!this.process.client_id) return;
            this.claimSearch.loading = true;
            try {
                const result = await axios.get('/api/admin/other-proceedings/claims', { params: { client_id: this.process.client_id, term: this.claimSearch.term, limit: 30 } });
                this.claimSearch.items = result.data.content.data;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 3000 });
            } finally {
                this.claimSearch.loading = false;
            }
        },

        fillPartyFromClaim(claim) {
            if (!claim) return;
            this.draft.name = claim.name;
            this.draft.registration = claim.registration;
            this.draft.claim_ids = [claim.id];
            this.draft.person_type = String(claim.registration || '').replace(/\D/g, '').length === 14 ? 2 : 1;
        },

        claimLabel(claim) {
            if (!claim) return '';
            const parts = [claim.name, this.formatRegistration(claim.registration), this.formatCurrency(claim.amount, claim.currency), this.className(claim.type)];
            if (claim.company_name) parts.push(claim.company_name);
            return parts.filter(Boolean).join(' · ');
        },

        // ---------- formatacao ----------

        className(type) {
            const found = this.classes.find((item) => Number(item.id) === Number(type));
            return found ? found.name : (type ? `Classe ${type}` : '—');
        },

        eventTypeName(type) {
            return labelOf(eventTypes, type, 'Pedido');
        },

        eventStatusName(status) {
            return labelOf(eventStatuses, status, '');
        },

        formatCurrency(value, currency = 1) {
            const symbol = Number(currency) === 2 ? 'US$' : (Number(currency) === 3 ? '€' : 'R$');
            return `${symbol} ${Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },

        formatRegistration(value) {
            const clean = String(value || '').replace(/\D/g, '');
            if (clean.length === 11) return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
            if (clean.length === 14) return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
            return value || '';
        },

        formatDate(value) {
            return value ? this.$moment(String(value).slice(0, 10)).format('DD/MM/YYYY') : '';
        },

        formatDateTime(value) {
            if (!value) return '';
            const raw = String(value);
            // Datas digitadas (sem fuso) vem como "YYYY-MM-DD HH:mm:ss"; as geradas pelo servidor vem em ISO (UTC).
            return raw.includes('T') ? this.$moment(raw).format('DD/MM/YYYY HH:mm') : this.$moment(raw.slice(0, 16), 'YYYY-MM-DD HH:mm').format('DD/MM/YYYY HH:mm');
        },

        toInputDate(value) {
            return value ? String(value).slice(0, 10) : '';
        },

        toInputDateTime(value) {
            if (!value) return '';
            const raw = String(value);
            return raw.includes('T') ? this.$moment(raw).format('YYYY-MM-DDTHH:mm') : raw.slice(0, 16).replace(' ', 'T');
        },
    },
};
</script>

<style scoped>
.op-overview { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; margin-bottom: 10px; }
.op-overview--ops { margin-bottom: 18px; }
.op-overview__item { display: flex; align-items: center; padding: 12px 14px; border: 1px solid #e4e7ec; border-radius: 10px; background: #fff; }
.op-overview__item > div { display: flex; flex-direction: column; }
.op-overview__label { display: block; font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.04em; color: #667085; margin-bottom: 2px; }
.op-overview__value { font-size: 14px; font-weight: 600; color: #101828; }
.op-overview:not(.op-overview--ops) .op-overview__item { flex-direction: column; align-items: flex-start; }
.op-tabs { border-bottom: 1px solid #e4e7ec; }
.op-tab-count { margin-left: 6px; padding: 1px 7px; border-radius: 999px; background: #eef0f4; font-size: 11px; font-weight: 600; color: #475467; }
.op-info-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 14px 24px; }
.op-info-grid > div { display: flex; flex-direction: column; font-size: 13.5px; }
.op-info-grid__wide { grid-column: 1 / -1; }
.op-pre { white-space: pre-wrap; }
.op-danger { color: #dc2626; }
.op-subtitle { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: #475467; margin-bottom: 6px; }
.op-party, .op-impact { padding: 14px 16px; border: 1px solid #e4e7ec; border-radius: 10px; background: #fff; }
.op-party + .op-party, .op-impact + .op-impact { margin-top: 10px; }
.op-party__head, .op-impact__head { display: flex; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.op-party__actions { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }
.op-party__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 12px; padding-top: 12px; border-top: 1px dashed #e4e7ec; }
@media (max-width: 960px) { .op-party__grid { grid-template-columns: 1fr; } }
.op-line { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding: 4px 0; font-size: 13px; }
.op-line > span:first-child { flex: 1 1 auto; min-width: 140px; }
.op-row-click { cursor: pointer; }
.op-review-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px; }
.op-review-card { padding: 12px 14px; border: 1px solid #e4e7ec; border-radius: 10px; background: #fafbfc; font-size: 13px; }
.op-review-card--removed { border-color: #fecaca; background: #fef2f2; }
.op-review-card--new { border-color: #bbf7d0; background: #f0fdf4; }
</style>
